import { useState, useCallback } from 'react';
import { buildImportPlan } from '../lib/stackImport';

export function useStackImportActions({ stored, upd, showToast }) {
  const [showImportSheet, setShowImportSheet] = useState(false);
  const [importFiles, setImportFiles] = useState({ stackItems: null, injectableVials: null, pillBottles: null });
  const [importPlan, setImportPlan] = useState(null);
  const [importParseError, setImportParseError] = useState('');

  const readFile = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsText(file);
  });

  const parseImportFiles = useCallback(async () => {
    setImportParseError(''); setImportPlan(null);
    if (!importFiles.stackItems) { setImportParseError('Stack Items CSV is required.'); return; }
    try {
      const [stackItemsCsv, injectableVialsCsv, pillBottlesCsv] = await Promise.all([
        readFile(importFiles.stackItems),
        importFiles.injectableVials ? readFile(importFiles.injectableVials) : Promise.resolve(null),
        importFiles.pillBottles ? readFile(importFiles.pillBottles) : Promise.resolve(null),
      ]);
      const plan = buildImportPlan({
        stackItemsCsv, injectableVialsCsv, pillBottlesCsv,
        existingCompounds: stored.compounds, existingVials: stored.vials,
      });
      setImportPlan(plan);
    } catch (err) {
      setImportParseError(err.message || 'Could not parse the uploaded files.');
    }
  }, [importFiles, stored]);

  const commitImport = useCallback(() => {
    if (!importPlan) return;
    const compounds = { ...stored.compounds };
    const counters = { ...stored.counters };
    const vials = { ...stored.vials };
    for (const { key, compound } of importPlan.compoundsToCreate) {
      compounds[key] = compound;
      if (!(key in counters)) counters[key] = 0;
      if (!(key in vials)) vials[key] = [];
    }
    for (const { compoundKey, vial } of importPlan.vialsToCreate) {
      vials[compoundKey] = [...(vials[compoundKey] || []), vial];
    }
    upd({ compounds, counters, vials, stackImported: true });
    showToast(`Imported ${importPlan.summary.compounds} stack item${importPlan.summary.compounds === 1 ? '' : 's'}, ${importPlan.summary.vials} vial/bottle${importPlan.summary.vials === 1 ? '' : 's'} ✓`);
    setShowImportSheet(false); setImportFiles({ stackItems: null, injectableVials: null, pillBottles: null }); setImportPlan(null);
  }, [importPlan, stored, upd, showToast]);

  return { showImportSheet, setShowImportSheet, importFiles, setImportFiles, importPlan, setImportPlan, importParseError, setImportParseError, parseImportFiles, commitImport };
}
